import { useState, useCallback } from "react";
import {
  Flame, Eye, Heart, MessageCircle, Share2, ExternalLink,
  Loader2, Search, Globe, AlertCircle, Sparkles, Wand2,
  TrendingUp, Lightbulb, ChevronDown, ChevronUp, Copy, Check,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";

// ---- constants ----

const PLATFORM_OPTIONS = [
  { value: "both",      label: "TikTok + Instagram" },
  { value: "tiktok",   label: "TikTok" },
  { value: "instagram",label: "Instagram" },
];

const LANGUAGE_OPTIONS = [
  { value: "nl", label: "Nederlands" },
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
];

const PERIOD_OPTIONS = [
  { value: "dag",   label: "Voorbije 24 uur" },
  { value: "week",  label: "Voorbije week" },
  { value: "maand", label: "Voorbije maand" },
];

const NICHE_LABELS = {
  auto: "Automotive", mode: "Mode & Fashion", food: "Food & Horeca",
  fitness: "Sport & Fitness", beauty: "Beauty & Wellness", reizen: "Reizen & Travel",
  interieur: "Interieur & Design", business: "Business", tech: "Technologie",
  muziek: "Muziek", immo: "Vastgoed", gezondheid: "Gezondheid",
  diy: "DIY & Renovatie", gaming: "Gaming", dieren: "Dieren",
  kunst: "Kunst & Cultuur", marketing: "Marketing", sport: "Sport",
};

const PLATFORM_BADGE = {
  tiktok:    { bg: "bg-pink-500/10",    text: "text-pink-500",    border: "border-pink-500/20" },
  instagram: { bg: "bg-purple-500/10", text: "text-purple-500", border: "border-purple-500/20" },
};

// ---- helpers ----

function fmt(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function useCopy() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);
  return { copy, copied };
}

// ---- sub-components ----

const VideoCard = ({ video, rank, isDark }) => {
  const colors = PLATFORM_BADGE[video.platform] || PLATFORM_BADGE.instagram;
  return (
    <a
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "group flex flex-col rounded-xl border overflow-hidden transition-all hover:scale-[1.015]",
        isDark
          ? "bg-white/[0.03] border-white/[0.06] hover:border-white/[0.14]"
          : "bg-white border-gray-200/80 hover:border-gray-300 hover:shadow-sm"
      )}
    >
      <div className="relative aspect-[9/16] overflow-hidden bg-gray-900">
        {video.thumbnail_url
          ? <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
          : <div className={cn("w-full h-full flex items-center justify-center", isDark ? "bg-white/[0.04]" : "bg-gray-100")}><Flame className="h-8 w-8 opacity-20" /></div>
        }
        <div className={cn(
          "absolute top-2 left-2 h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold",
          rank <= 3 ? "bg-amber-400 text-amber-900" : isDark ? "bg-black/60 text-white" : "bg-white/80 text-gray-700"
        )}>{rank}</div>
        <div className={cn("absolute top-2 right-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border", colors.bg, colors.text, colors.border)}>
          {video.platform}
        </div>
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <ExternalLink className="h-6 w-6 text-white drop-shadow" />
        </div>
      </div>
      <div className="flex-1 p-3 space-y-1.5">
        {video.author_handle && (
          <p className={cn("text-[11px] font-medium truncate", isDark ? "text-gray-400" : "text-gray-500")}>
            @{video.author_handle}
          </p>
        )}
        {video.caption && (
          <p className={cn("text-[12px] leading-relaxed line-clamp-2", isDark ? "text-gray-300" : "text-gray-700")}>
            {video.caption}
          </p>
        )}
        <div className={cn("flex items-center gap-3 text-[11px]", isDark ? "text-gray-500" : "text-gray-400")}>
          {video.views > 0 && <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{fmt(video.views)}</span>}
          <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{fmt(video.likes)}</span>
          {video.comments > 0 && <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{fmt(video.comments)}</span>}
          {video.shares > 0 && <span className="flex items-center gap-1"><Share2 className="h-3 w-3" />{fmt(video.shares)}</span>}
        </div>
      </div>
    </a>
  );
};

const AnalysisPanel = ({ analysis, isDark }) => {
  const [open, setOpen] = useState(true);
  if (!analysis) return null;
  return (
    <div className={cn("rounded-xl border overflow-hidden", isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/70")}>
      <button
        onClick={() => setOpen(v => !v)}
        className={cn("w-full flex items-center justify-between px-4 py-3 border-b transition-colors", isDark ? "border-white/[0.06] hover:bg-white/[0.03]" : "border-gray-100 hover:bg-gray-50")}
      >
        <div className="flex items-center gap-2">
          <TrendingUp className={cn("h-4 w-4", isDark ? "text-blue-400" : "text-blue-500")} />
          <span className={cn("text-[14px] font-semibold", isDark ? "text-white" : "text-gray-900")}>AI Trend Analyse</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-500/30 text-blue-500">AI</Badge>
        </div>
        {open ? <ChevronUp className="h-4 w-4 opacity-40" /> : <ChevronDown className="h-4 w-4 opacity-40" />}
      </button>

      {open && (
        <div className="p-4 space-y-4">
          {/* Samenvatting */}
          {analysis.samenvatting && (
            <div className={cn("rounded-lg p-3", isDark ? "bg-blue-500/10 border border-blue-500/20" : "bg-blue-50 border border-blue-100")}>
              <p className={cn("text-[13px] leading-relaxed", isDark ? "text-blue-200" : "text-blue-800")}>
                {analysis.samenvatting}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Waarom trending */}
            {analysis.waarom_trending?.length > 0 && (
              <div>
                <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-2", isDark ? "text-gray-500" : "text-gray-400")}>Waarom trending</p>
                <ul className="space-y-1.5">
                  {analysis.waarom_trending.map((r, i) => (
                    <li key={i} className={cn("flex items-start gap-1.5 text-[12.5px]", isDark ? "text-gray-300" : "text-gray-700")}>
                      <span className="text-amber-500 mt-0.5">▸</span>{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Hoe inspelen */}
            {analysis.hoe_inspelen?.length > 0 && (
              <div>
                <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-2", isDark ? "text-gray-500" : "text-gray-400")}>Hoe inspelen</p>
                <ul className="space-y-1.5">
                  {analysis.hoe_inspelen.map((r, i) => (
                    <li key={i} className={cn("flex items-start gap-1.5 text-[12.5px]", isDark ? "text-gray-300" : "text-gray-700")}>
                      <span className="text-emerald-500 mt-0.5">✓</span>{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Trending thema's */}
          {analysis.trending_themas?.length > 0 && (
            <div>
              <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-2", isDark ? "text-gray-500" : "text-gray-400")}>Trending thema&apos;s</p>
              <div className="flex flex-wrap gap-1.5">
                {analysis.trending_themas.map((t, i) => (
                  <Badge key={i} variant="outline" className={cn("text-[11px]", isDark ? "border-white/10 text-gray-300" : "")}>
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Aanbevolen hooks */}
          {analysis.aanbevolen_hooks?.length > 0 && (
            <div>
              <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-2", isDark ? "text-gray-500" : "text-gray-400")}>Aanbevolen hooks</p>
              <div className="space-y-1.5">
                {analysis.aanbevolen_hooks.map((h, i) => (
                  <div key={i} className={cn("px-3 py-2 rounded-lg text-[12.5px] italic", isDark ? "bg-white/[0.04] text-gray-300" : "bg-gray-50 text-gray-700")}>
                    &ldquo;{h}&rdquo;
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Posting tip */}
          {analysis.optimale_posting_tip && (
            <div className={cn("flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px]", isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-300" : "bg-emerald-50 border border-emerald-100 text-emerald-800")}>
              <Lightbulb className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{analysis.optimale_posting_tip}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const IdeaPanel = ({ idea, isDark }) => {
  const { copy, copied } = useCopy();
  if (!idea) return null;
  return (
    <div className={cn("rounded-xl border overflow-hidden", isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/70")}>
      <div className={cn("flex items-center justify-between px-4 py-3 border-b", isDark ? "border-white/[0.06]" : "border-gray-100")}>
        <div className="flex items-center gap-2">
          <Wand2 className={cn("h-4 w-4", isDark ? "text-purple-400" : "text-purple-500")} />
          <span className={cn("text-[14px] font-semibold", isDark ? "text-white" : "text-gray-900")}>Gegenereerd video-idee</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-500/30 text-purple-500">AI</Badge>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Titel */}
        {idea.titel && (
          <div>
            <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-1", isDark ? "text-gray-500" : "text-gray-400")}>Titel</p>
            <p className={cn("text-[15px] font-semibold", isDark ? "text-white" : "text-gray-900")}>{idea.titel}</p>
          </div>
        )}

        {/* Hook */}
        {idea.hook && (
          <div>
            <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-1.5", isDark ? "text-gray-500" : "text-gray-400")}>Hook (eerste 3 seconden)</p>
            <div className={cn("px-3 py-2.5 rounded-lg text-[13px] font-medium italic", isDark ? "bg-purple-500/10 border border-purple-500/20 text-purple-200" : "bg-purple-50 border border-purple-100 text-purple-900")}>
              &ldquo;{idea.hook}&rdquo;
            </div>
          </div>
        )}

        {/* Concept */}
        {idea.concept && (
          <div>
            <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-1", isDark ? "text-gray-500" : "text-gray-400")}>Concept</p>
            <p className={cn("text-[13px] leading-relaxed", isDark ? "text-gray-300" : "text-gray-700")}>{idea.concept}</p>
          </div>
        )}

        {/* Structuur */}
        {idea.structuur?.length > 0 && (
          <div>
            <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-2", isDark ? "text-gray-500" : "text-gray-400")}>Video structuur</p>
            <div className="space-y-1.5">
              {idea.structuur.map((s, i) => (
                <div key={i} className={cn("flex items-start gap-2 text-[12.5px]", isDark ? "text-gray-300" : "text-gray-700")}>
                  <span className={cn("shrink-0 font-bold text-[10px] mt-0.5 px-1.5 py-0.5 rounded", isDark ? "bg-white/[0.08] text-gray-300" : "bg-gray-100 text-gray-600")}>
                    {i + 1}
                  </span>
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Caption */}
        {idea.caption_voorstel && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className={cn("text-[11px] uppercase tracking-wider font-semibold", isDark ? "text-gray-500" : "text-gray-400")}>Caption voorstel</p>
              <button
                onClick={() => copy(idea.caption_voorstel)}
                className={cn("flex items-center gap-1 text-[11px] px-2 py-0.5 rounded transition-colors", isDark ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100")}
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? "Gekopieerd" : "Kopieer"}
              </button>
            </div>
            <div className={cn("px-3 py-2.5 rounded-lg text-[12.5px] leading-relaxed whitespace-pre-wrap", isDark ? "bg-white/[0.04] text-gray-300" : "bg-gray-50 text-gray-700")}>
              {idea.caption_voorstel}
            </div>
          </div>
        )}

        {/* Hashtags */}
        {idea.hashtags?.length > 0 && (
          <div>
            <p className={cn("text-[11px] uppercase tracking-wider font-semibold mb-2", isDark ? "text-gray-500" : "text-gray-400")}>Hashtags</p>
            <div className="flex flex-wrap gap-1.5">
              {idea.hashtags.map((h, i) => (
                <Badge key={i} variant="outline" className={cn("text-[11px] cursor-pointer", isDark ? "border-white/10 text-blue-400 hover:border-blue-500/40" : "text-blue-600")}
                  onClick={() => copy(h)}
                >
                  {h}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Platform tip */}
          {idea.platform_tip && (
            <div className={cn("flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px]", isDark ? "bg-blue-500/10 border border-blue-500/20 text-blue-300" : "bg-blue-50 border border-blue-100 text-blue-800")}>
              <Lightbulb className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <div><span className="font-semibold">Platform:</span> {idea.platform_tip}</div>
            </div>
          )}
          {/* Waarom nu */}
          {idea.waarom_nu && (
            <div className={cn("flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px]", isDark ? "bg-amber-500/10 border border-amber-500/20 text-amber-300" : "bg-amber-50 border border-amber-100 text-amber-800")}>
              <Flame className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <div><span className="font-semibold">Waarom nu:</span> {idea.waarom_nu}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- main component ----

const ViralDiscoverTab = ({
  isDark, discoverViral, analyseViralTrends, generateViralIdea,
  // state lifted to parent for persistence across tab switches
  videos, setVideos, analysis, setAnalysis, idea, setIdea,
  filters, setFilters, searched, setSearched,
}) => {
  const { platform, niche, language, period } = filters;
  const setPlatform = (v) => setFilters(f => ({ ...f, platform: v }));
  const setNiche    = (v) => setFilters(f => ({ ...f, niche: v }));
  const setLanguage = (v) => setFilters(f => ({ ...f, language: v }));
  const setPeriod   = (v) => setFilters(f => ({ ...f, period: v }));

  const [loadingVideos,   setLoadingVideos]   = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingIdea,     setLoadingIdea]     = useState(false);

  const [error, setError]                 = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [ideaError, setIdeaError]         = useState(null);

  const params = { platform, niche, language, period };

  const handleDiscover = useCallback(async () => {
    if (!niche) return;
    setLoadingVideos(true);
    setError(null);
    setAnalysis(null);
    setIdea(null);
    setSearched(true);
    try {
      const res = await discoverViral({ ...params, limit: 100 });
      setVideos(res.videos || []);
    } catch (e) {
      const code = e?.response?.data?.code;
      setError(
        code === "RAPIDAPI_NOT_CONFIGURED"
          ? "RAPIDAPI_KEY is niet geconfigureerd op de server."
          : code === "RATE_LIMITED"
          ? "RapidAPI rate limit bereikt op de gratis tier. Wacht 1 minuut en probeer opnieuw, of upgrade je plan."
          : code === "RAPIDAPI_FORBIDDEN"
          ? "Geen toegang tot de API (403). Controleer of je gesubscribed bent op RapidAPI."
          : e?.response?.data?.message || "Ophalen mislukt."
      );
      setVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  }, [discoverViral, params, niche]);

  const handleAnalyse = useCallback(async () => {
    if (!videos.length) return;
    setLoadingAnalysis(true);
    setAnalysisError(null);
    setIdea(null);
    try {
      const res = await analyseViralTrends({ ...params, videos });
      setAnalysis(res);
    } catch (e) {
      setAnalysisError(e?.response?.data?.message || "Analyse mislukt.");
    } finally {
      setLoadingAnalysis(false);
    }
  }, [analyseViralTrends, params, videos]);

  const handleGenerateIdea = useCallback(async () => {
    if (!videos.length) return;
    setLoadingIdea(true);
    setIdeaError(null);
    try {
      const res = await generateViralIdea({ ...params, videos, analysis });
      setIdea(res);
    } catch (e) {
      setIdeaError(e?.response?.data?.message || "Generatie mislukt.");
    } finally {
      setLoadingIdea(false);
    }
  }, [generateViralIdea, params, videos, analysis]);

  const tiktokCount = videos.filter(v => v.platform === "tiktok").length;
  const igCount     = videos.filter(v => v.platform === "instagram").length;

  return (
    <div className="space-y-4">

      {/* ---- Filter bar ---- */}
      <div className={cn("rounded-xl border p-4", isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/70")}>
        <div className="flex items-center gap-2 mb-3">
          <Flame className={cn("h-4 w-4", isDark ? "text-orange-400" : "text-orange-500")} />
          <h2 className={cn("text-[14px] font-semibold", isDark ? "text-white" : "text-gray-900")}>Viraal ontdekken</h2>
          <p className={cn("text-[12px] ml-1", isDark ? "text-gray-500" : "text-gray-400")}>
            Top 100 viralste video&apos;s per niche, platform en tijdsperiode
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className={cn("text-[11px] font-medium", isDark ? "text-gray-500" : "text-gray-500")}>Platform</label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="h-8 w-[170px] text-[12px]"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORM_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-[12px]">{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className={cn("text-[11px] font-medium", isDark ? "text-gray-500" : "text-gray-500")}>Niche / sector</label>
            <Select value={niche} onValueChange={setNiche}>
              <SelectTrigger className="h-8 w-[190px] text-[12px]"><SelectValue placeholder="Kies een niche…" /></SelectTrigger>
              <SelectContent>{Object.entries(NICHE_LABELS).map(([k, l]) => <SelectItem key={k} value={k} className="text-[12px]">{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className={cn("text-[11px] font-medium", isDark ? "text-gray-500" : "text-gray-500")}>Taal</label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="h-8 w-[145px] text-[12px]"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGUAGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-[12px]"><span className="flex items-center gap-1.5"><Globe className="h-3 w-3" />{o.label}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className={cn("text-[11px] font-medium", isDark ? "text-gray-500" : "text-gray-500")}>Periode</label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-8 w-[170px] text-[12px]"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-[12px]">{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <Button
            size="sm"
            onClick={handleDiscover}
            disabled={!niche || loadingVideos}
            className="gap-1.5 h-8 bg-orange-500 hover:bg-orange-600 text-white"
          >
            {loadingVideos ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            {loadingVideos ? "Laden…" : "Ontdek top 100"}
          </Button>
        </div>
      </div>

      {/* ---- Fetch error ---- */}
      {error && (
        <div className={cn("flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px]", isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700")}>
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* ---- Results header + AI buttons ---- */}
      {!loadingVideos && videos.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={cn("text-[12px]", isDark ? "text-gray-400" : "text-gray-500")}>{videos.length} video&apos;s</span>
            {tiktokCount > 0 && <Badge variant="outline" className="text-[11px] text-pink-500 border-pink-500/30 bg-pink-500/5">TikTok {tiktokCount}</Badge>}
            {igCount > 0 && <Badge variant="outline" className="text-[11px] text-purple-500 border-purple-500/30 bg-purple-500/5">Instagram {igCount}</Badge>}
            <span className={cn("text-[11px]", isDark ? "text-gray-600" : "text-gray-400")}>· 6u cache</span>
          </div>

          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={handleAnalyse}
              disabled={loadingAnalysis}
              className="gap-1.5 h-8 text-[12px]"
            >
              {loadingAnalysis ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {loadingAnalysis ? "Analyseren…" : "Analyseer trends"}
              <Badge variant="outline" className="ml-1 text-[9px] px-1 h-[14px] border-amber-500/40 text-amber-500">1 credit</Badge>
            </Button>

            <Button
              size="sm"
              onClick={handleGenerateIdea}
              disabled={loadingIdea}
              className="gap-1.5 h-8 text-[12px] bg-purple-600 hover:bg-purple-700 text-white"
            >
              {loadingIdea ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              {loadingIdea ? "Genereren…" : "Genereer video-idee op basis van mijn content"}
              <Badge variant="outline" className="ml-1 text-[9px] px-1 h-[14px] border-white/30 text-white/70">1 credit</Badge>
            </Button>
          </div>
        </div>
      )}

      {/* ---- Analysis errors ---- */}
      {analysisError && (
        <div className={cn("flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px]", isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700")}>
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /><span>{analysisError}</span>
        </div>
      )}
      {ideaError && (
        <div className={cn("flex items-start gap-2 px-3 py-2.5 rounded-lg text-[12px]", isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700")}>
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /><span>{ideaError}</span>
        </div>
      )}

      {/* ---- AI Analyse panel ---- */}
      <AnalysisPanel analysis={analysis} isDark={isDark} />

      {/* ---- Video idee panel ---- */}
      <IdeaPanel idea={idea} isDark={isDark} />

      {/* ---- Loading skeleton ---- */}
      {loadingVideos && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className={cn("rounded-xl border overflow-hidden animate-pulse", isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200")}>
              <div className={cn("aspect-[9/16]", isDark ? "bg-white/[0.05]" : "bg-gray-100")} />
              <div className="p-3 space-y-2">
                <div className={cn("h-2 w-2/3 rounded", isDark ? "bg-white/[0.06]" : "bg-gray-100")} />
                <div className={cn("h-2 w-full rounded", isDark ? "bg-white/[0.04]" : "bg-gray-100")} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Video grid ---- */}
      {!loadingVideos && videos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {videos.map((video, i) => (
            <VideoCard key={video.id || i} video={video} isDark={isDark} rank={i + 1} />
          ))}
        </div>
      )}

      {/* ---- Empty after search ---- */}
      {!loadingVideos && searched && videos.length === 0 && !error && (
        <div className={cn("text-center py-16 rounded-xl border", isDark ? "text-gray-500 border-white/[0.06] bg-white/[0.02]" : "text-gray-400 border-gray-200 bg-white")}>
          <Flame className="h-8 w-8 mx-auto mb-3 opacity-30" />
          <p className="text-[13px]">Geen video&apos;s gevonden voor deze combinatie.</p>
          <p className="text-[12px] mt-1">Probeer een andere niche, periode of taal.</p>
        </div>
      )}

      {/* ---- Initial empty ---- */}
      {!loadingVideos && !searched && (
        <div className={cn("text-center py-20 rounded-xl border", isDark ? "text-gray-500 border-white/[0.06] bg-white/[0.02]" : "text-gray-400 border-gray-200 bg-white")}>
          <Flame className="h-10 w-10 mx-auto mb-3 opacity-20" />
          <p className={cn("text-[14px] font-medium mb-1", isDark ? "text-gray-400" : "text-gray-600")}>Ontdek de viralste video&apos;s</p>
          <p className="text-[12px]">Kies platform, niche, taal en periode — klik daarna op <strong>Ontdek top 100</strong>.</p>
          <div className={cn("mt-4 flex flex-wrap justify-center gap-2 text-[11px]", isDark ? "text-gray-600" : "text-gray-400")}>
            <span>Daarna kan je de trends laten analyseren door AI</span>
            <span>·</span>
            <span>en een video-idee laten genereren op basis van jouw eigen content</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViralDiscoverTab;
